import { useEffect, useState, useCallback, useMemo } from "react";
import { api, apiError } from "../lib/api";
import SearchSelect from "../components/SearchSelect";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import {
  Palette, Plus, X, CheckCircle, XCircle, Sparkles,
  ArrowRight, Layers, Paintbrush, Scissors, ChevronLeft,
  Link2, Clock, History,
} from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  accepted: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
  deferred: "bg-zinc-100 text-zinc-700",
};

export default function DesignStudio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [components, setComponents] = useState([]);
  const [collections, setCollections] = useState([]);

  // Form state for editing
  const [silhouette, setSilhouette] = useState("");
  const [opForm, setOpForm] = useState({ name: "", description: "" });
  const [showAddOp, setShowAddOp] = useState(false);
  const [componentTypes, setComponentTypes] = useState([]);
  const [materialMasters, setMaterialMasters] = useState([]);
  const [colourStandards, setColourStandards] = useState([]);
  const [compForm, setCompForm] = useState({ component_id: "", placement: "" });
  // The chosen master record, not a typed string — see SearchSelect.
  const [pickedComp, setPickedComp] = useState(null);
  const [pickedMat, setPickedMat] = useState(null);
  const [pickedCol, setPickedCol] = useState(null);
  const [showAddComp, setShowAddComp] = useState(false);
  const [matForm, setMatForm] = useState({ material_name: "", placement: "" });
  const [showAddMat, setShowAddMat] = useState(false);
  const [showAddCol, setShowAddCol] = useState(false);

  // Create design modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ title: "", category: "tops", description: "" });
  const [creating, setCreating] = useState(false);

  // Collection linking
  const [selectedCollId, setSelectedCollId] = useState("");
  const [linkingCollection, setLinkingCollection] = useState(false);

  // Version history
  const [versions, setVersions] = useState([]);

  const loadDesigns = useCallback(() => {
    setLoading(true);
    api.get("/design-studio/designs")
      .then((r) => setDesigns(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadDesign = useCallback((designId) => {
    setLoading(true);
    Promise.all([
      api.get(`/design-studio/designs/${designId}`),
      api.get("/garment-components?status=published"),
      api.get("/collections"),
      // Governed masters the pickers below select from. Each is tolerated as empty: a project
      // that has not populated a master should see a message saying where to populate it, not a
      // broken picker.
      api.get("/component-types").catch(() => ({ data: [] })),
      api.get("/material-masters").catch(() => ({ data: [] })),
      api.get("/colour-standards").catch(() => ({ data: [] })),
    ]).then(([dr, cr, colr, ctr, mmr, csr]) => {
      setDesign(dr.data);
      setSilhouette(dr.data.silhouette || "");
      setComponents(cr.data);
      setCollections(colr.data);
      setComponentTypes(ctr.data || []);
      setMaterialMasters(mmr.data || []);
      setColourStandards(csr.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
    // Fetch versions separately (best-effort)
    api.get(`/design-studio/designs/${designId}/versions`)
      .then((r) => setVersions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setVersions([]));
  }, []);

  useEffect(() => {
    if (id) {
      loadDesign(id);
    } else {
      loadDesigns();
    }
  }, [id, loadDesign, loadDesigns]);

  const updateDesign = async (updates) => {
    if (!design) return;
    setSaving(true);
    try {
      const r = await api.patch(`/design-studio/designs/${design.id}`, updates);
      setDesign(r.data);
      toast.success("Design updated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveSilhouette = () => {
    updateDesign({ silhouette });
  };

  const addConstructionOp = () => {
    if (!opForm.name) return;
    const ops = [...(design.construction_operations || []), { name: opForm.name, description: opForm.description }];
    updateDesign({ construction_operations: ops });
    setOpForm({ name: "", description: "" });
    setShowAddOp(false);
  };

  const removeOp = (idx) => {
    const ops = (design.construction_operations || []).filter((_, i) => i !== idx);
    updateDesign({ construction_operations: ops });
  };

  const addComponent = () => {
    if (!compForm.component_id) return;
    const comp = components.find((c) => c.id === compForm.component_id);
    const entry = {
      component_entry_id: compForm.component_id,
      display_name: comp?.display_name || "",
      placement: compForm.placement,
    };
    const comps = [...(design.components || []), entry];
    updateDesign({ components: comps });
    setCompForm({ component_id: "", placement: "" });
    setShowAddComp(false);
  };

  const removeComponent = (idx) => {
    const comps = (design.components || []).filter((_, i) => i !== idx);
    updateDesign({ components: comps });
  };

  // ── governed option lists ───────────────────────────────────────────────────────────────
  const typeNameById = useMemo(
    () => Object.fromEntries((componentTypes || []).map((t) => [t.id, t.name])),
    [componentTypes]
  );

  const componentOptions = useMemo(
    () => (components || [])
      .filter((c) => c.status === "published")
      .map((c) => {
        const type = typeNameById[c.component_type_id] || "Other";
        return { id: c.id, label: c.display_name, sublabel: type, group: type.toUpperCase(), raw: c };
      }),
    [components, typeNameById]
  );

  const materialOptions = useMemo(
    () => (materialMasters || []).map((m) => {
      const composition = Array.isArray(m.fibre_composition)
        ? m.fibre_composition.map((f) => (typeof f === "string" ? f : [f.percentage, f.fibre].filter(Boolean).join("% "))).join(", ")
        : "";
      return {
        id: m.id,
        label: m.name,
        sublabel: [m.material_type, m.construction_type, composition].filter(Boolean).join(" · "),
        raw: m,
      };
    }),
    [materialMasters]
  );

  const colourOptions = useMemo(
    () => (colourStandards || []).map((c) => ({
      id: c.id,
      label: c.colour_code,
      sublabel: c.reference_system || "",
      swatch: c.hex_code || "",
      raw: c,
    })),
    [colourStandards]
  );

  const addMaterial = () => {
    if (!pickedMat) return;
    // material_id is the governed reference; material_name is kept so existing rows and the
    // list above still render, and so a later master rename is visible as a divergence.
    const entry = {
      material_id: pickedMat.id,
      material_name: pickedMat.label,
      composition: pickedMat.sublabel || "",
      placement: matForm.placement,
    };
    const mats = [...(design.material_selections || []), entry];
    updateDesign({ material_selections: mats });
    setMatForm({ material_name: "", placement: "" });
    setPickedMat(null);
    setShowAddMat(false);
  };

  const removeMaterial = (idx) => {
    const mats = (design.material_selections || []).filter((_, i) => i !== idx);
    updateDesign({ material_selections: mats });
  };

  const addColour = () => {
    if (!pickedCol) return;
    const entry = {
      colour_id: pickedCol.id,
      colour_code: pickedCol.label,
      colourway_name: pickedCol.label,
      hex_code: pickedCol.swatch || "",
      reference_system: pickedCol.sublabel || "",
    };
    const cols = [...(design.colour_selections || []), entry];
    updateDesign({ colour_selections: cols });
    setPickedCol(null);
    setShowAddCol(false);
  };

  const removeColour = (idx) => {
    const cols = (design.colour_selections || []).filter((_, i) => i !== idx);
    updateDesign({ colour_selections: cols });
  };

  const routeToTechpack = async () => {
    if (!design) return;
    setSaving(true);
    try {
      await api.post(`/design-studio/designs/${design.id}/route-to-techpack`);
      toast.success("Design routed to tech pack generation");
      loadDesign(design.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const acceptSuggestion = async (suggestionId) => {
    try {
      await api.post(`/design-studio/designs/${design.id}/suggestions/${suggestionId}/accept`);
      toast.success("Suggestion accepted");
      loadDesign(design.id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const rejectSuggestion = async (suggestionId) => {
    try {
      await api.post(`/design-studio/designs/${design.id}/suggestions/${suggestionId}/reject`, { rejection_reason: "" });
      toast.success("Suggestion rejected");
      loadDesign(design.id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createDesign = async () => {
    if (!createForm.title || !createForm.category) return;
    setCreating(true);
    try {
      const r = await api.post("/design-studio/designs", {
        title: createForm.title,
        category: createForm.category,
        description: createForm.description || undefined,
      });
      toast.success("Style card created");
      setShowCreate(false);
      setCreateForm({ title: "", category: "tops", description: "" });
      navigate(`/studio/design/${r.data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  const linkCollection = async () => {
    if (!selectedCollId || !design) return;
    setLinkingCollection(true);
    try {
      await api.post(`/collections/${selectedCollId}/designs`, { design_id: design.id });
      toast.success("Design linked to collection");
      loadDesign(design.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLinkingCollection(false);
    }
  };

  // ── List View ──
  if (!id) {
    return (
      <div className="p-10 max-w-6xl mx-auto">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">AI</p>
            <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Design Studio</h1>
            <p className="text-zinc-500 mt-2 text-sm">Compose garment designs: select silhouette, add components, assign materials and colours.</p>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
            <Plus size={15} /> Create Style Card
          </button>
        </div>
        {loading ? (
          <div className="mt-10 text-sm text-zinc-400">Loading...</div>
        ) : designs.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <Palette size={26} className="mx-auto mb-3" />
            <p className="text-sm">No designs yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {designs.map((d) => (
              <button key={d.id} onClick={() => navigate(`/studio/design/${d.id}`)}
                className="text-left border border-zinc-200 bg-white rounded-lg p-5 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium">{d.title}</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">{d.category} · {d.silhouette || "No silhouette"}</p>
                  </div>
                  {d.ready_for_techpack && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-800">
                      Ready for TP
                    </span>
                  )}
                </div>
                <div className="flex gap-3 mt-3 text-[10px] text-zinc-400">
                  <span>{(d.components || []).length} components</span>
                  <span>{(d.material_selections || []).length} materials</span>
                  <span>{(d.colour_selections || []).length} colours</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Create Style Card Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
            <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-semibold text-lg">Create Style Card</h2>
                <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
              </div>
              <div className="space-y-4">
                <input value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} placeholder="Title"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                <select value={createForm.category} onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="tops">Tops</option>
                  <option value="bottoms">Bottoms</option>
                  <option value="dresses">Dresses</option>
                  <option value="outerwear">Outerwear</option>
                  <option value="accessories">Accessories</option>
                </select>
                <textarea value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Description (optional)" rows={3}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
                <button onClick={createDesign} disabled={!createForm.title || creating}
                  className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                  {creating ? "Creating..." : "Create Style Card"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Detail / Edit View ──
  if (loading || !design) {
    return <div className="p-10 text-sm text-zinc-400">Loading design...</div>;
  }

  const suggestions = design.suggestions || [];

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <button onClick={() => navigate("/studio/design")}
        className="text-xs text-zinc-400 hover:text-zinc-700 inline-flex items-center gap-1 mb-4">
        <ChevronLeft size={12} /> Back to designs
      </button>

      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Design Studio</p>
          <h1 className="font-heading text-2xl font-bold mt-1 tracking-tight">{design.title}</h1>
          <p className="text-zinc-400 text-xs mt-1">{design.category} · {design.owner_name}</p>
        </div>
        <button onClick={routeToTechpack} disabled={saving || design.ready_for_techpack}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">
          <ArrowRight size={15} /> {design.ready_for_techpack ? "Routed to Tech Pack" : "Route to Tech Pack"}
        </button>
      </div>

      {/* Collection Linking */}
      <div className="mt-6 border border-zinc-200 bg-white rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-zinc-400">
            <Link2 size={12} /> Collection
          </div>
          {design.collection_id ? (
            <span className="text-sm text-zinc-700">
              Linked to: <span className="font-medium">{collections.find((c) => c.id === design.collection_id)?.name || design.collection_id}</span>
            </span>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <select value={selectedCollId} onChange={(e) => setSelectedCollId(e.target.value)}
                className="flex-1 border border-zinc-200 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select a collection...</option>
                {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button onClick={linkCollection} disabled={!selectedCollId || linkingCollection}
                className="px-3 py-1.5 bg-zinc-950 text-white rounded-md text-xs font-medium hover:bg-zinc-800 disabled:opacity-40">
                {linkingCollection ? "Linking..." : "Link to Collection"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Silhouette */}
        <div className="border border-zinc-200 bg-white rounded-lg p-6">
          <h3 className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3 flex items-center gap-2">
            <Scissors size={12} /> Silhouette
          </h3>
          <div className="flex gap-2">
            <input value={silhouette} onChange={(e) => setSilhouette(e.target.value)} placeholder="e.g. A-line, Fitted, Boxy"
              className="flex-1 border border-zinc-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            <button onClick={saveSilhouette} disabled={saving}
              className="px-3 py-2 bg-zinc-100 text-zinc-700 rounded-md text-sm hover:bg-zinc-200">Save</button>
          </div>
        </div>

        {/* Construction Operations */}
        <div className="border border-zinc-200 bg-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-[0.15em] text-zinc-400 flex items-center gap-2">
              <Layers size={12} /> Construction Operations ({(design.construction_operations || []).length})
            </h3>
            <button onClick={() => setShowAddOp(true)} className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1">
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="space-y-1">
            {(design.construction_operations || []).map((op, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1.5 px-2 bg-zinc-50 rounded">
                <span>{op.name}{op.description ? ` — ${op.description}` : ""}</span>
                <button onClick={() => removeOp(i)} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>
              </div>
            ))}
          </div>
          {showAddOp && (
            <div className="mt-3 space-y-2">
              <input value={opForm.name} onChange={(e) => setOpForm({ ...opForm, name: e.target.value })} placeholder="Operation name"
                className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" />
              <input value={opForm.description} onChange={(e) => setOpForm({ ...opForm, description: e.target.value })} placeholder="Description (optional)"
                className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button onClick={addConstructionOp} className="px-3 py-1.5 bg-zinc-950 text-white rounded-md text-xs">Add</button>
                <button onClick={() => setShowAddOp(false)} className="px-3 py-1.5 text-zinc-400 text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* Components from Library */}
        <div className="border border-zinc-200 bg-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-[0.15em] text-zinc-400 flex items-center gap-2">
              Components ({(design.components || []).length})
            </h3>
            <button onClick={() => setShowAddComp(true)} className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1">
              <Plus size={12} /> Select from Library
            </button>
          </div>
          <div className="space-y-1">
            {(design.components || []).map((comp, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1.5 px-2 bg-zinc-50 rounded">
                <span>{comp.display_name || comp.component_entry_id}{comp.placement ? ` (${comp.placement})` : ""}</span>
                <button onClick={() => removeComponent(i)} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>
              </div>
            ))}
          </div>
          {showAddComp && (
            <div className="mt-3 space-y-2">
              <SearchSelect
                options={componentOptions}
                value={pickedComp}
                onChange={(o) => { setPickedComp(o); setCompForm({ ...compForm, component_id: o ? o.id : "" }); }}
                placeholder="Search components — try “rag” or “collar”…"
                emptyMessage="No components in the library. Publish components in Components first."
                grouped
                autoFocus
              />
              <input value={compForm.placement} onChange={(e) => setCompForm({ ...compForm, placement: e.target.value })} placeholder="Placement (e.g. front, collar)"
                className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button onClick={addComponent} className="px-3 py-1.5 bg-zinc-950 text-white rounded-md text-xs">Add</button>
                <button onClick={() => setShowAddComp(false)} className="px-3 py-1.5 text-zinc-400 text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* Material Selections */}
        <div className="border border-zinc-200 bg-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-[0.15em] text-zinc-400 flex items-center gap-2">
              <Paintbrush size={12} /> Materials ({(design.material_selections || []).length})
            </h3>
            <button onClick={() => setShowAddMat(true)} className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1">
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="space-y-1">
            {(design.material_selections || []).map((mat, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1.5 px-2 bg-zinc-50 rounded">
                <span>{mat.material_name || mat.name}{mat.placement ? ` (${mat.placement})` : ""}</span>
                <button onClick={() => removeMaterial(i)} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>
              </div>
            ))}
          </div>
          {showAddMat && (
            <div className="mt-3 space-y-2">
              <SearchSelect
                options={materialOptions}
                value={pickedMat}
                onChange={setPickedMat}
                placeholder="Search the material master…"
                emptyMessage="No materials in library. Add materials in Materials Master first."
                autoFocus
              />
              {/* Placement stays free text: it describes THIS garment, not a master record. */}
              <input value={matForm.placement} onChange={(e) => setMatForm({ ...matForm, placement: e.target.value })} placeholder="Placement (e.g. body, collar, cuff)"
                className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button onClick={addMaterial} className="px-3 py-1.5 bg-zinc-950 text-white rounded-md text-xs">Add</button>
                <button onClick={() => setShowAddMat(false)} className="px-3 py-1.5 text-zinc-400 text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* Colour Selections */}
        <div className="border border-zinc-200 bg-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-[0.15em] text-zinc-400 flex items-center gap-2">
              <Palette size={12} /> Colours ({(design.colour_selections || []).length})
            </h3>
            <button onClick={() => setShowAddCol(true)} className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1">
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="space-y-1">
            {(design.colour_selections || []).map((col, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1.5 px-2 bg-zinc-50 rounded">
                <span className="flex items-center gap-2">
                  {col.hex_code && (
                    <span className="w-3 h-3 rounded-full border border-zinc-300" style={{ backgroundColor: col.hex_code }} />
                  )}
                  {col.colourway_name || col.colour_code}
                  {col.reference_system && <span className="text-xs text-zinc-400">{col.reference_system}</span>}
                </span>
                <button onClick={() => removeColour(i)} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>
              </div>
            ))}
          </div>
          {showAddCol && (
            <div className="mt-3 space-y-2">
              <SearchSelect
                options={colourOptions}
                value={pickedCol}
                onChange={setPickedCol}
                placeholder="Search colour standards…"
                emptyMessage="No colour standards defined. Add colours in Colours & Lab Dips first."
                autoFocus
              />
              <div className="flex gap-2">
                <button onClick={addColour} className="px-3 py-1.5 bg-zinc-950 text-white rounded-md text-xs">Add</button>
                <button onClick={() => setShowAddCol(false)} className="px-3 py-1.5 text-zinc-400 text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* AI Suggestions */}
        <div className="border border-zinc-200 bg-white rounded-lg p-6">
          <h3 className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3 flex items-center gap-2">
            <Sparkles size={12} /> AI Suggestions ({suggestions.length})
          </h3>
          {suggestions.length === 0 ? (
            <p className="text-xs text-zinc-400">No suggestions yet. AI suggestions appear when gaps are detected in your design.</p>
          ) : (
            <div className="space-y-2">
              {suggestions.map((s) => (
                <div key={s.id} className="p-3 bg-zinc-50 rounded-md">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-medium text-zinc-500">{s.component_name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[s.outcome] || STATUS_STYLE.pending}`}>
                      {s.outcome}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-700">{s.suggestion_text}</p>
                  {s.outcome === "pending" && (
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => acceptSuggestion(s.id)}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100">
                        <CheckCircle size={10} /> Accept
                      </button>
                      <button onClick={() => rejectSuggestion(s.id)}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100">
                        <XCircle size={10} /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Version History */}
        <div className="border border-zinc-200 bg-white rounded-lg p-6">
          <h3 className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3 flex items-center gap-2">
            <History size={12} /> Version History ({versions.length})
          </h3>
          {versions.length === 0 ? (
            <p className="text-xs text-zinc-400">No version history available.</p>
          ) : (
            <div className="space-y-2">
              {versions.map((v, i) => (
                <div key={v.id || i} className="flex items-start gap-3 p-3 bg-zinc-50 rounded-md">
                  <div className="w-6 h-6 rounded-full bg-zinc-200 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[10px] font-bold text-zinc-600">{v.version_number || i + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-zinc-700">v{v.version_number || i + 1}</span>
                      {v.created_at && (
                        <span className="text-[10px] text-zinc-400 flex items-center gap-1">
                          <Clock size={9} /> {new Date(v.created_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {v.change_summary && <p className="text-xs text-zinc-500 mt-0.5">{v.change_summary}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
